import type { ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { focusRingInsetClass } from "@/components/ui";

export type StepCardProps = {
  stepNumber: number;
  completed: boolean;
  expanded: boolean;
  onHeaderClick: () => void;
};

/** Container for a vertical list of `SettlementStepCard` items. */
export const settlementStepListClass =
  "overflow-hidden rounded-lg border border-surface-200 bg-surface-0 divide-y divide-surface-200";

function StepIndicator({
  stepNumber,
  completed,
  expanded,
}: {
  stepNumber: number;
  completed: boolean;
  expanded: boolean;
}) {
  return (
    <span
      className={cn(
        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums transition-colors",
        completed
          ? "bg-success-50 text-success-700 ring-success-600/25 ring-1 ring-inset"
          : expanded
            ? "bg-primary-fill text-white shadow-xs"
            : "bg-surface-0 text-surface-500 ring-surface-300 ring-1 ring-inset",
      )}
      aria-hidden
    >
      {completed ? (
        <Check
          className="h-3.5 w-3.5"
          strokeWidth={2.5}
        />
      ) : (
        stepNumber
      )}
    </span>
  );
}

export function SettlementStepCard({
  stepNumber,
  completed = false,
  expanded = false,
  onHeaderClick,
  title,
  description,
  disabled = false,
  children,
}: StepCardProps & {
  title: string;
  description: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "transition-colors",
        expanded && "bg-surface-50/70 dark:bg-surface-100/30",
      )}
    >
      <button
        type="button"
        className={cn(
          "focus-visible:bg-surface-50 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
          focusRingInsetClass,
          !expanded && "hover:bg-surface-50 dark:hover:bg-surface-100/50",
        )}
        onClick={onHeaderClick}
        aria-expanded={expanded}
      >
        <StepIndicator
          stepNumber={stepNumber}
          completed={completed}
          expanded={expanded}
        />
        <span
          className={cn(
            "min-w-0 flex-1 text-sm font-medium tracking-tight",
            expanded
              ? "text-surface-900"
              : completed
                ? "text-surface-600"
                : "text-surface-700",
          )}
        >
          {title}
        </span>
        <ChevronDown
          className={cn(
            "text-surface-400 h-4 w-4 shrink-0 transition-transform duration-200",
            expanded && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {expanded && (
        <div
          className={cn(
            "space-y-3 pr-4 pb-4 pl-[52px] transition-opacity",
            disabled && "opacity-60",
          )}
        >
          <p className="text-surface-500 text-[13px] leading-relaxed">
            {description}
          </p>
          {children}
        </div>
      )}
    </div>
  );
}
